import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import VideoGallery from '../../components/feature/VideoGallery';
import { supabase } from '../../lib/supabase';
import type { Service, TeamMember, Testimonial, GalleryItem, Banner } from '../../lib/supabase';

interface PageContent {
  [key: string]: string;
}

interface Video {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  category: string | null;
  is_active: boolean;
  display_order: number;
}

export default function Home() {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [content, setContent] = useState<PageContent>({});
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState<any>(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  useEffect(() => {
    loadData();
    loadSiteSettings();
    
    // SEO meta tags güncelleme
    document.title = 'Gaziantep Oto Detaylandırma ve Seramik Kaplama | MRT Soft - Profesyonel Araç Bakım Hizmetleri';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Gaziantep\'in en iyi oto detaylandırma merkezi MRT Soft. Seramik kaplama, boya koruma filmi, iç temizlik, araç restorasyon ve profesyonel araç bakım hizmetleri. 15+ yıllık deneyim, 500+ mutlu müşteri.');
    }
  }, []);

  // Banner otomatik kayma - sadece birden fazla banner varsa
  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [banners.length]);

  const loadSiteSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error loading site settings:', error);
        return;
      }
      
      if (data) {
        setSiteSettings(data);
      }
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const [servicesRes, teamRes, testimonialsRes, galleryRes, bannersRes, videosRes, contentRes] = await Promise.all([
        supabase.from('services').select('*').eq('is_active', true).order('display_order').limit(6),
        supabase.from('team_members').select('*').eq('is_active', true).order('display_order').limit(3),
        supabase.from('testimonials').select('*').eq('is_active', true).order('display_order').limit(3),
        supabase.from('gallery').select('*').eq('is_active', true).order('display_order').limit(8),
        supabase.from('banners').select('*').eq('is_active', true).order('display_order'),
        supabase.from('videos').select('*').eq('is_active', true).order('display_order').limit(6),
        supabase.from('page_content').select('*').eq('page_name', 'home')
      ]);

      setServices(servicesRes.data || []);
      setTeam(teamRes.data || []);
      setTestimonials(testimonialsRes.data || []);
      setGallery(galleryRes.data || []);
      setBanners(bannersRes.data || []);
      setVideos(videosRes.data || []);

      const contentMap: PageContent = {};
      contentRes.data?.forEach((item) => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentBanner = banners[currentBannerIndex];
  
  const heroImage = currentBanner?.image_url || "https://public.readdy.ai/ai/img_res/7dbc17d71759738963626d3c7caeb379.jpg";
  const heroTitle = currentBanner?.title || 'Aracınızı Yeni Gibi Yapın';
  const heroSubtitle = currentBanner?.subtitle || 'Profesyonel otomotiv yenileme ve detaylı temizlik hizmetleri ile aracınızın değerini artırın';
  const heroButtonText = currentBanner?.button_text || 'Randevu Al';
  const heroButtonLink = currentBanner?.button_link || '/appointment';

  const nextBanner = () => {
    if (banners.length === 0) return;
    setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    if (banners.length === 0) return;
    setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToBanner = (index: number) => {
    if (banners.length === 0) return;
    setCurrentBannerIndex(index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section with Banner Slider */}
      <section 
        className="relative h-[70vh] md:h-[85vh] flex items-center justify-center pt-20"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center w-full">
          <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)] break-words px-4">
            <strong>Gaziantep Oto Detaylandırma</strong> - {heroTitle}
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-6 md:mb-8 max-w-3xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] px-4">
            <strong>Profesyonel seramik kaplama</strong>, <strong>boya koruma filmi</strong> ve <strong>araç bakım hizmetleri</strong> ile aracınızı yenileyin. {heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <button
              onClick={() => window.REACT_APP_NAVIGATE(heroButtonLink)}
              className="px-6 md:px-8 py-3 md:py-4 bg-[#FCD34D] text-black font-semibold rounded-lg hover:bg-[#FCD34D]/90 transition-all whitespace-nowrap cursor-pointer shadow-lg text-sm md:text-base"
            >
              {heroButtonText}
            </button>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/services')}
              className="px-6 md:px-8 py-3 md:py-4 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer border border-white/30 shadow-lg text-sm md:text-base"
            >
              Hizmetlerimiz
            </button>
          </div>
        </div>

        {/* Banner Navigation - Sadece birden fazla banner varsa göster */}
        {banners.length > 1 && (
          <>
            {/* Sol Ok */}
            <button
              onClick={prevBanner}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all cursor-pointer border border-white/30"
              aria-label="Önceki Afiş"
            >
              <i className="ri-arrow-left-s-line text-xl md:text-2xl text-white"></i>
            </button>

            {/* Sağ Ok */}
            <button
              onClick={nextBanner}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all cursor-pointer border border-white/30"
              aria-label="Sonraki Afiş"
            >
              <i className="ri-arrow-right-s-line text-xl md:text-2xl text-white"></i>
            </button>

            {/* Banner Göstergeleri */}
            <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToBanner(index)}
                  className={`h-2 md:h-3 rounded-full transition-all cursor-pointer ${
                    index === currentBannerIndex
                      ? 'bg-[#FCD34D] w-6 md:w-8'
                      : 'bg-white/50 hover:bg-white/70 w-2 md:w-3'
                  }`}
                  aria-label={`Afiş ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2 drop-shadow-lg">
                {content.stats_customers || content.stat1_value || '500+'}
              </div>
              <div className="text-gray-100 text-lg drop-shadow-md">
                <strong>Mutlu Müşteri</strong>
              </div>
            </div>
            <div className="text-center bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2 drop-shadow-lg">
                {content.stats_experience || content.stat2_value || '15+'}
              </div>
              <div className="text-gray-100 text-lg drop-shadow-md">
                <strong>Yıllık Deneyim</strong>
              </div>
            </div>
            <div className="text-center bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2 drop-shadow-lg">
                {content.stats_services || content.stat3_value || services.length.toString()}
              </div>
              <div className="text-gray-100 text-lg drop-shadow-md">
                <strong>Profesyonel Hizmet</strong>
              </div>
            </div>
            <div className="text-center bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="text-5xl font-bold text-[#FCD34D] mb-2 drop-shadow-lg">
                {content.stats_rating || content.stat4_value || '100%'}
              </div>
              <div className="text-gray-100 text-lg drop-shadow-md">
                <strong>Müşteri Memnuniyeti</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Gallery Section */}
      <div className="bg-white">
        <VideoGallery videos={videos} />
      </div>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <strong>Gaziantep Oto Detaylandırma Hizmetlerimiz</strong>
            </h3>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              <strong>Seramik kaplama</strong>, <strong>boya koruma</strong>, <strong>iç temizlik</strong> ve daha fazlası için profesyonel araç bakım çözümleri
            </p>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <i className="ri-loader-4-line text-4xl text-[#FCD34D] animate-spin"></i>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {services.map((service) => (
                  <article key={service.id} className="bg-white rounded-lg overflow-hidden hover:transform hover:scale-105 transition-all cursor-pointer group shadow-lg border border-gray-100">
                    <div className="w-full h-64 overflow-hidden">
                      <img 
                        src={service.image_url} 
                        alt={`${service.title} - Gaziantep Oto Detaylandırma`}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg mb-4">
                        <i className={`${service.icon} text-2xl text-[#FCD34D]`}></i>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2"><strong>{service.title}</strong></h4>
                      <p className="text-gray-600">{service.description}</p>
                    </div>
                  </article>
                ))}
              </div>
              
              <div className="text-center mt-12">
                <Link 
                  to="/services" 
                  className="whitespace-nowrap inline-block px-8 py-4 bg-[#FCD34D] text-black font-semibold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer"
                >
                  Tüm Hizmetleri Görüntüle
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Neden Bizi Seçmelisiniz?
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Aracınız için en iyi hizmeti sunmak için çalışıyoruz
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-md p-8 rounded-lg border border-white/10">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg mb-6">
                <i className="ri-award-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Profesyonel Ekip</h3>
              <p className="text-gray-300">
                Uzman ekibimiz, aracınıza en iyi bakımı sağlamak için deneyim ve tutkuyla çalışır
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-md p-8 rounded-lg border border-white/10">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg mb-6">
                <i className="ri-shield-check-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Kaliteli Ürünler</h3>
              <p className="text-gray-300">
                Sadece premium kalitede ürünler kullanarak aracınızın uzun ömürlü korumasını sağlıyoruz
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-md p-8 rounded-lg border border-white/10">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg mb-6">
                <i className="ri-time-line text-3xl text-[#FCD34D]"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Hızlı Hizmet</h3>
              <p className="text-gray-300">
                Zamanınıza değer veriyor ve işlerinizi zamanında teslim ediyoruz
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Çalışma Sürecimiz
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Basit ve etkili 4 adımlık sürecimiz
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D] rounded-full mx-auto mb-6">
                <span className="text-3xl font-bold text-black">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Randevu Alın</h3>
              <p className="text-gray-600">
                Size uygun bir tarih ve saat seçin
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D] rounded-full mx-auto mb-6">
                <span className="text-3xl font-bold text-black">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Araç Teslimi</h3>
              <p className="text-gray-600">
                Aracınızı tesisimize getirin
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D] rounded-full mx-auto mb-6">
                <span className="text-3xl font-bold text-black">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Profesyonel Bakım</h3>
              <p className="text-gray-600">
                Uzman ekibimiz işe koyulur
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-[#FCD34D] rounded-full mx-auto mb-6">
                <span className="text-3xl font-bold text-black">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Araç Teslim</h3>
              <p className="text-gray-600">
                Aracınızı yeni gibi teslim alın
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Çalışmalarımız
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Yaptığımız işlere göz atın
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.map((item) => (
                <div key={item.id} className="relative overflow-hidden rounded-lg group cursor-pointer h-80 shadow-lg">
                  <img 
                    src={item.image_url} 
                    alt={item.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white">{item.title}</h3>
                      {item.description && (
                        <p className="text-sm text-gray-300 mt-2">{item.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Müşteri Yorumları
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Müşterilerimizin bizim hakkımızda söyledikleri
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-gray-50 p-8 rounded-lg shadow-lg border border-gray-100">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#FCD34D] text-xl"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">&quot;{testimonial.comment}&quot;</p>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="font-bold text-gray-900">{testimonial.customer_name}</div>
                    <div className="text-sm text-gray-600">{testimonial.service_name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Team Section */}
      {team.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Ekibimiz
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Profesyonel ve deneyimli ekibimizle tanışın
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member) => (
                <div key={member.id} className="bg-white rounded-lg overflow-hidden group cursor-pointer shadow-lg border border-gray-100">
                  <div className="w-full h-96 overflow-hidden">
                    <img 
                      src={member.image_url} 
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-[#FCD34D] mb-3">{member.position}</p>
                    {member.bio && (
                      <p className="text-sm text-gray-600">{member.bio}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Location Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Bizi Ziyaret Edin
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Gaziantep'teki işletmemize bekleriz
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Harita */}
            <div className="w-full h-80 lg:h-96 rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3172.5!2d37.3833!3d37.0667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1531e6e2e7a0a0a1%3A0x1234567890abcdef!2sMRT%20Soft!5e0!3m2!1str!2str!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MRT Soft Konum"
              ></iframe>
            </div>
            
            {/* İletişim Bilgileri */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-[#FCD34D]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Adres</h3>
                  <p className="text-gray-600">
                    {siteSettings?.contact_address || 'Gaziantep, Türkiye'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-[#FCD34D]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Telefon</h3>
                  <p className="text-gray-600">
                    +90 545 745 50 75
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/20 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-2xl text-[#FCD34D]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Çalışma Saatleri</h3>
                  <p className="text-gray-600 whitespace-pre-line">
                    {siteSettings?.working_hours || 'Pazartesi - Cuma: 08:00 - 18:00\nCumartesi: 10:00 - 17:00\nPazar: Kapalı'}
                  </p>
                </div>
              </div>
              
              <Link 
                to="/contact" 
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-[#FCD34D] text-black font-semibold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer mt-4"
              >
                <i className="ri-arrow-right-line"></i>
                İletişime Geç
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Aracınız İçin Randevu Alın
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Profesyonel araç bakım hizmetlerimizden yararlanmak için hemen randevu oluşturun
          </p>
          <Link 
            to="/appointment" 
            className="whitespace-nowrap inline-block px-10 py-5 bg-[#FCD34D] text-black font-bold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer text-lg"
          >
            Hemen Randevu Al
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
